import { useEffect, useMemo, useState } from "react";
import { Table } from "../../../../components/table";
import { H3, Label, Th } from "../../../../components/typography/styled";
import { Layout } from "../../../../containers/app/layout";
import { BillingWrapper } from "./styled";
import { retrieveAllBilling } from "../../../../utils/apis/billing/retrieveAllBilling";
import Cookies from "universal-cookie";
import { Row } from "../../../../components/flex/styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseInput } from "../../../../components/form/input/styled";

export const Billing = () => {
  const PAGE_SIZE = 10;
  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");

  const [currentPage, setCurrentPage] = useState(1);
  const [billings, setBillings] = useState({
    creditPurchases: [],
    totalCreditAmount: 0,
    totalCreditCost: 0,
  });
  const [filter, setFilter] = useState({
    value: "all time",
    companyName: "",
  });

  useEffect(() => {
    retrieveAllBilling(TOKEN, filter)
      .then((data) => {
        setBillings(data);
      })
      .catch((err) => {
        console.error("Failed to fetch billings:", err);
      });
  }, [TOKEN, filter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1);
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const totalBillings = billings?.creditPurchases?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalBillings / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedBillings = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return billings?.creditPurchases?.slice(start, end);
  }, [billings, currentPage]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 1, currentPage, currentPage + 1];
  }, [currentPage, totalPages]);

  const rangeStart =
    totalBillings === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalBillings);
  const billingContextCount = totalBillings === 1 ? "billing" : "billings";

  return (
    <Layout id={"billing"} title={"Billing"}>
      <BillingWrapper>
        <Row>
          <div className="heading-row">
            <H3>Billings List</H3>
          </div>
        </Row>
        <Row className="filter-row">
          <div className="filter">
            <BaseFieldSet>
              <Label htmlFor="companyName">Company Name</Label>
              <BaseInput
                type="text"
                name="companyName"
                placeholder="Search by Company Name"
                value={filter.companyName}
                onChange={handleChange}
              />
            </BaseFieldSet>
            <BaseFieldSet>
              <Label htmlFor="value">Filter by Date</Label>
              <BaseSelect
                name="value"
                onChange={(e) => handleChange(e)}
                value={filter.value}
              >
                <option value="all time">All time</option>
                <option value="last day">Last one day</option>
                <option value="last week">Last week</option>
                <option value="last month">Last month</option>
                <option value="last three month">Last three months</option>
                <option value="last six month">Last six months</option>
                <option value="last year">Last year</option>
              </BaseSelect>
            </BaseFieldSet>
          </div>
          <div className="billing-summary">
            <table className="billing-summary-table">
              <thead>
                <tr>
                  <Th>Total Credit Purchased</Th>
                  <Th>Total Credit Amount</Th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{billings?.totalCreditAmount.toLocaleString() ?? 0}</td>
                  <td>{billings?.totalCreditCost.toLocaleString() ?? 0}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Row>
        <div className="billing-table">
          <Table
            columnTitles={[
              "Company Name",
              "Employer Email",
              "Credit Amount",
              "Cash Amount",
              "Date Initiated",
              "Date Completed",
              "Plan",
            ]}
            rowItems={paginatedBillings ?? []}
            location={"Billing Table"}
          />
          <div className="table-footer">
            <span>
              Showing {rangeStart}-{rangeEnd} of {totalBillings}{" "}
              {billingContextCount}
            </span>
            <div className="pagination" aria-hidden="true">
              <button
                type="button"
                className="page-chip"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                &lt;
              </button>
              {visiblePageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={`page-chip ${currentPage === pageNumber ? "active" : ""}`}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                className="page-chip"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </BillingWrapper>
    </Layout>
  );
};
